import { useState } from "react";
import { confirmAction } from "@/components/app/Confirm";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";

const CATS = ["Strong Fit", "Potential Fit", "Needs Review", "Not a Fit", "Not a Fit – Knockout"];

export function OverrideDialog({ app, open, onOpenChange }) {
  const qc = useQueryClient();
  const [category, setCategory] = useState(app.category_override?.category || "Potential Fit");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const refresh = () => { qc.invalidateQueries({ queryKey: ["candidate"] }); qc.invalidateQueries({ queryKey: ["ranked", app.job_id] }); };
  const save = async () => {
    if (reason.trim().length < 3) return toast.error("Add a reason for the override");
    setBusy(true);
    try {
      await api.post(`/applications/${app.id}/category-override`, { category, reason });
      toast.success(`Category set to ${category}`);
      refresh();
      onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const clear = async () => {
    if (!(await confirmAction({ title: "Remove the category override?", body: "The AI category is used again.", confirmLabel: "Remove override" }))) return;
    try { await api.delete(`/applications/${app.id}/category-override`); toast.success("Override removed"); refresh(); onOpenChange(false); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-[6px]" data-testid="override-dialog">
        <DialogHeader><DialogTitle>Override category</DialogTitle></DialogHeader>
        <select value={category} onChange={(e) => setCategory(e.target.value)} className="h-9 w-full rounded-lg border border-line px-2 text-sm" data-testid="override-category-select">
          {CATS.map((c) => <option key={c}>{c}</option>)}
        </select>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why are you changing it? (required, saved to the audit log)" data-testid="override-reason-input" />
        <DialogFooter className="gap-2">
          {app.category_override && <Button variant="ghost" onClick={clear} data-testid="override-clear-btn">Remove override</Button>}
          <Button onClick={save} disabled={busy} data-testid="override-save-btn">Save override</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
